/**
 * События модуля `points`: Inngest (durable workflows) + Socket.IO (push в открытую вкладку).
 *
 * ЭТОТ ФАЙЛ — ЗЕРКАЛО КАТАЛОГА `apps/server/src/inngest/client.ts`, а не
 * отдельная вселенная. До правки он описывал события, которых не существует
 * (`points/entry.created`, `points/expiry.scan`, `points/migrate.requested`), и
 * поля, которых в реальных payload'ах нет (`sourceClass`, `basisMinor`,
 * `suggestedPoints`) — Schema-First при этом формально «соблюдался», потому что
 * `AssertRouterContract` покрывает только tRPC (аудит points.md В9).
 *
 * Правило: имя и форма здесь обязаны совпадать с каталогом ДО СИМВОЛА. Правки
 * идут парой.
 *
 * Все начисляющие события идемпотентны по `idempotencyKey` — ретрай Inngest не
 * должен давать второе начисление (грабли №5 `lessons-learned.md`: двойное
 * начисление Баллов — прямой убыток).
 */
import { z } from "zod";
import {
	IsoDateTimeSchema,
	PointsAmountSchema,
	ServiceIdSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";
import { IdempotencyKeySchema, PointsBalanceSchema, PointsPositiveSchema } from "./schemas.js";

// ───────────────────────────── Inngest ─────────────────────────────

/**
 * Запрос начисления по факту, присланному сервисом экосистемы.
 *
 * `kind` — ровно два значения. `migrate` и `adjust` убраны как неаудируемый путь
 * записи произвольной суммы, `earn_referral` — потому что класс A считается от
 * нашей чистой части платежа, которой в событии нет; реферальные начисления идут
 * публичным входом модуля, а не событием (аудит К1/К7).
 *
 * `amount` — ПРЕДЛОЖЕНИЕ сервиса. Решение принимает `points`: множитель IEC+,
 * суточный потолок, месячный бюджет (инвариант №9). Для вехи число игнорируется
 * полностью — сумма берётся из `MILESTONE_REWARDS` по типу вехи.
 */
export const PointsEarnRequestedEventSchema = z.object({
	name: z.literal("points/earn.requested"),
	data: z.object({
		userSub: SubjectSchema,
		serviceId: ServiceIdSchema,
		kind: z.enum(["earn_activity", "earn_milestone"]),
		amount: z.number().int(),
		reasonCode: z.string().min(1).max(64),
		/** Для вехи — её тип (`programmeMastered` и т.п.), иначе тип ссылки. */
		referenceType: z.string().min(1).max(64).nullish(),
		referenceId: z.string().min(1).max(128).nullish(),
		idempotencyKey: IdempotencyKeySchema,
		occurredAt: IsoDateTimeSchema,
	}),
});

/**
 * Сторно строки ledger обратной записью (инвариант №11). Исходная строка не
 * редактируется никогда; ключ сторно строится ОТ СТРОКИ (`reverse:<entryId>`),
 * поэтому два запроса на одну строку дают одну обратную запись.
 */
export const PointsReverseRequestedEventSchema = z.object({
	name: z.literal("points/reverse.requested"),
	data: z.object({
		entryId: UuidV7Schema,
		reason: z.string().min(1).max(128),
		idempotencyKey: IdempotencyKeySchema,
	}),
});

/** Внеплановый прогон сгорания (догон за пропущенные сутки). Обычный запуск — cron. */
export const PointsExpireScanEventSchema = z.object({
	name: z.literal("points/expire.scan"),
	data: z.object({ asOf: IsoDateTimeSchema.optional() }),
});

/**
 * Баланс изменился. Наблюдательное событие: подписчика в Inngest у него нет по
 * замыслу — сервисы узнают об изменении SET-событием `points.balance-changed`,
 * а витрина — Socket.IO ниже.
 */
export const PointsBalanceChangedEventSchema = z.object({
	name: z.literal("points/balance.changed"),
	data: z.object({ userSub: SubjectSchema, balance: PointsBalanceSchema }),
});

// ───────────────────────────── Socket.IO ─────────────────────────────

/**
 * Баланс виден и в витрине, и в меню аккаунта каждого сервиса (грабли №9) —
 * push обновляет обе поверхности.
 */
export const PointsBalanceChangedSocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("points:balance-changed"),
	payload: z.object({
		balance: PointsBalanceSchema,
		delta: PointsAmountSchema,
	}),
});

/**
 * Грант выпущен/освобождён — модалка погашения обновляется без перезагрузки.
 *
 * ВНИМАНИЕ: сервер это событие пока НЕ эмитит — Composition Root ограничивается
 * логом (`grantIssued` / `grantReleased`). Схема оставлена как контракт для
 * подключения шва, а не как описание работающего поведения.
 */
export const PointsGrantChangedSocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("points:grant-changed"),
	payload: z.object({
		grantId: UuidV7Schema,
		state: z.enum(["issued", "applied", "expired", "refunded"]),
		pointsSpent: PointsPositiveSchema,
		changedAt: IsoDateTimeSchema,
	}),
});
